import { Link, useParams, useSearchParams } from "react-router-dom";
import { Languages } from "lucide-react";
import { BUSINESS } from "@/components/Footer";
import { currentLang, legalT } from "@/i18n";

const UPDATED = "6 June 2026";
const VARS = { product: BUSINESS.product, name: BUSINESS.name, abn: BUSINESS.abn, location: BUSINESS.location, email: BUSINESS.email };

// Wraps the support email inside a translated sentence in a mailto link.
export const withEmail = (text) => {
  const parts = text.split(BUSINESS.email);
  if (parts.length === 1) return text;
  return parts.flatMap((p, i) => (i === 0 ? [p] : [<a key={i} className="underline" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>, p]));
};

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="font-display text-xl font-semibold">{title}</h2>
    <div className="text-slate-700 leading-relaxed space-y-2">{children}</div>
  </section>
);

const PAGES = {
  privacy: { titleKey: "privacy_policy", sections: ["who", { k: "collect", list: 4 }, "children", "safe", "sharing", "choices", "retention"] },
  terms: { titleKey: "terms_of_service", sections: ["service", "accounts", "credits", "use", "content", "ai", "liability", "changes"] },
  refunds: { titleKey: "refund_policy", sections: ["rights", "failed", "mind", "faults", "paid"] },
};

export const TranslationNotice = ({ forceEn, lang }) => {
  if (lang === "en") return null;
  const tt = legalT(forceEn);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-2xl border-2 border-[#FFD166] bg-[#FFF9E6] px-4 py-3 text-sm mb-6" data-testid="legal-translation-notice">
      <Languages className="w-4 h-4 shrink-0 text-[#1E293B]" />
      <span className="flex-1 min-w-[200px]">{tt("legal_translation_notice")}</span>
      <Link to={forceEn ? "?" : "?lang=en"} className="font-bold underline hover:text-[#FF5252]" data-testid="legal-toggle-english">
        {forceEn ? tt("legal_view_translated") : tt("legal_view_english")}
      </Link>
    </div>
  );
};

export default function LegalPage({ kind }) {
  const params = useParams();
  const [search] = useSearchParams();
  const key = PAGES[kind || params.kind] ? kind || params.kind : "privacy";
  const page = PAGES[key];
  const lang = currentLang();
  const forceEn = lang !== "en" && search.get("lang") === "en";
  const tt = legalT(forceEn);
  const title = tt(page.titleKey);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid={`legal-page-${key}`} dir={forceEn ? "ltr" : undefined} lang={forceEn ? "en" : undefined}>
      <p className="text-sm font-bold uppercase tracking-wider text-[#FF5252] mb-2">{BUSINESS.product} · {tt("legal")}</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2" data-testid="legal-title">{title}</h1>
      <p className="text-sm text-slate-500 mb-8">{tt("legal_last_updated", { date: UPDATED })} · {BUSINESS.name} · ABN {BUSINESS.abn}</p>
      <TranslationNotice forceEn={forceEn} lang={lang} />
      <div className="card-soft p-6 sm:p-10 space-y-8" data-testid="legal-body">
        {page.sections.map((s) => {
          const id = typeof s === "string" ? s : s.k;
          const base = `legal_${key}_${id}`;
          return (
            <Section key={id} title={tt(`${base}_title`)}>
              {typeof s === "string" ? (
                <p>{withEmail(tt(`${base}_body`, VARS))}</p>
              ) : (
                <ul className="list-disc pl-6 space-y-1">
                  {Array.from({ length: s.list }, (_, i) => {
                    const text = tt(`${base}_li${i + 1}`);
                    const m = text.match(/\s*(?:——|—|:\s|：)\s*/);
                    return <li key={i}>{m ? <><b>{text.slice(0, m.index)}</b>{m[0]}{text.slice(m.index + m[0].length)}</> : text}</li>;
                  })}
                </ul>
              )}
            </Section>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 mt-8 text-sm font-bold">
        {Object.entries(PAGES).filter(([k]) => k !== key).map(([k, p]) => <Link key={k} to={`/${k}${forceEn ? "?lang=en" : ""}`} className="underline hover:text-[#FF5252]">{tt(p.titleKey)}</Link>)}
      </div>
    </div>
  );
}
